import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API, authHeaders } from '../api/ticketsApi';

function authConfig(params = {}) {
  return {
    ...authHeaders(),
    params,
  };
}

function isReady(lookup, params) {
  switch (lookup) {
    case 'domains':
      return true;
    case 'categories':
      return Boolean(params.domain);
    case 'services':
      return Boolean(params.domain && params.category);
    case 'field-definitions':
      return Boolean(params.domain);
    default:
      return false;
  }
}

function lookupUrl(lookup) {
  return `${API}/lookups/${lookup}`;
}

function normalizeLookupOptions(lookup, data) {
  if (!Array.isArray(data)) return [];
  if (lookup === 'domains') {
    return data.map((item) => {
      if (typeof item === 'string') return { key: item, label: item };
      return {
        ...item,
        key: item.key ?? item.code,
        label: item.label ?? item.name ?? item.key ?? item.code ?? '',
      };
    });
  }
  return data;
}

/**
 * Cascading ticket lookup fetcher. Re-fetches when parent params change.
 * Returns { options, loading, error, refetch }.
 */
export function useTicketLookup(lookup, params = {}, enabled = true) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const paramKey = JSON.stringify(params);
  const ready = enabled && isReady(lookup, params);

  const fetchOptions = useCallback(async () => {
    if (!ready) {
      setOptions([]);
      setLoading(false);
      setError(null);
      return;
    }

    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(lookupUrl(lookup), authConfig(params));
      if (id === requestId.current) {
        setOptions(normalizeLookupOptions(lookup, res.data));
      }
    } catch (e) {
      if (id === requestId.current) {
        const detail = e.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to load dropdown options');
        setOptions([]);
      }
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  }, [lookup, paramKey, ready]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return { options, loading, error, refetch: fetchOptions };
}

export { fetchDuplicates } from '../api/ticketsApi';
